import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { apiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Star, CheckCircle2, Loader2, Upload, X } from "lucide-react";

export default function ReviewPage() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState("");
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto(data.url);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Upload failed"); }
    setUploading(false);
    e.target.value = "";
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { setCtx((await api.get(`/reviews/${token}`)).data); }
    catch (err) { setCtx(null); toast.error(apiError(err.response?.data?.detail) || "This review link is invalid"); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (rating < 1) { toast.error("Please pick a star rating"); return; }
    setBusy(true);
    try { await api.post(`/reviews/${token}`, { rating, text: text.trim(), photo_url: photo || null }); setDone(true); toast.success("Thanks for your review!"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not submit"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-400"><Loader2 className="animate-spin mr-2" />Loading…</div>;
  if (!ctx) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-400" data-testid="review-invalid">This review link is invalid or has expired.</div>;

  if (done || ctx.already_submitted) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="review-thanks">
        <CheckCircle2 className="mx-auto text-[#10b981]" size={48} />
        <h1 className="font-display text-3xl font-black text-white mt-4">Thank you!</h1>
        <p className="text-zinc-400 mt-2">Your review has been received. We really appreciate you taking the time.</p>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-12" data-testid="review-page">
      <p className="text-xs font-mono-p uppercase tracking-widest text-[#ff6a13]">Order {ctx.order_number}</p>
      <h1 className="font-display text-3xl sm:text-4xl font-black text-white mt-2">Leave a review</h1>
      <p className="text-zinc-400 mt-2">Hi {ctx.customer_name}, how was your experience? Your review may appear on our website.</p>

      <div className="mt-6 bg-[#12141a] border border-zinc-800 rounded-2xl p-6">
        <p className="text-sm text-zinc-300 mb-2">Your rating</p>
        <div className="flex gap-1" data-testid="review-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} data-testid={`review-star-${n}`} className="transition-transform hover:scale-110">
              <Star size={34} className={(hover || rating) >= n ? "text-[#ffd600] fill-[#ffd600]" : "text-zinc-600"} />
            </button>
          ))}
        </div>
        <p className="text-sm text-zinc-300 mt-5 mb-2">Tell us more (optional)</p>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid="review-text" placeholder="What did you love? How were the prints and the service?" className="bg-[#1e222d] border-zinc-700 text-white" />
        <p className="text-sm text-zinc-300 mt-5 mb-2">Add a photo of your prints (optional)</p>
        {photo ? (
          <div className="relative inline-block">
            <div className="checker rounded-xl overflow-hidden"><img src={fileUrl(photo)} alt="Your print" className="max-h-40 object-contain" data-testid="review-photo-preview" /></div>
            <button type="button" onClick={() => setPhoto("")} data-testid="review-photo-remove" className="absolute -top-2 -right-2 bg-zinc-900 border border-zinc-700 rounded-full p-1 text-zinc-300 hover:text-red-400"><X size={14} /></button>
          </div>
        ) : (
          <label data-testid="review-photo-upload" className="inline-flex items-center gap-2 cursor-pointer text-sm text-zinc-300 border border-dashed border-zinc-700 rounded-xl px-4 py-3 hover:border-[#ff6a13]/60">
            {uploading ? <><Loader2 size={16} className="animate-spin" />Uploading…</> : <><Upload size={16} />Choose a photo</>}
            <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} />
          </label>
        )}
        <Button onClick={submit} disabled={busy} data-testid="review-submit" className="mt-4 bg-[#ff6a13] hover:bg-[#ff8a3d] disabled:opacity-60 text-white rounded-full h-12 px-8 font-semibold">Submit review</Button>
      </div>
    </div>
  );
}
